import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Box,
  Chip,
  CircularProgress,
  Alert
} from "@mui/material";
import { Close as CloseIcon, DescriptionOutlined as DocIcon } from "@mui/icons-material";
import { BASE_URL } from "../../../services/configUrls";
import TrainerDocumentEditor from "../TrainerDocumentEditor";
import { checkPrerequisitesViewEligibility } from "./FDPEDP/geoTagWatermarkUtil";

const DomainPrerequisitesViewModal = ({
  open,
  onClose,
  booking,
  domainId: propDomainId,
  domainName: propDomainName,
  eventType = "fdp"
}) => {
  const [resolvedDomainId, setResolvedDomainId] = useState(
    propDomainId || booking?.domain_id || booking?.domainId || null
  );
  const [resolvingDomain, setResolvingDomain] = useState(false);

  const domainName =
    propDomainName ||
    booking?.domain_name ||
    booking?.program_name ||
    booking?.domain ||
    "Domain";

  useEffect(() => {
    if (!open) return;

    const directId =
      propDomainId ||
      booking?.domain_id ||
      booking?.domainId ||
      (typeof booking?.domain === "number" ? booking.domain : null);

    if (directId) {
      setResolvedDomainId(directId);
      return;
    }

    // If numerical domain_id is not directly in the booking object, look it up by domain name
    let isMounted = true;
    const findDomainId = async () => {
      setResolvingDomain(true);
      try {
        const token = localStorage.getItem("accessToken");
        const headers = {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        };
        const type =
          eventType === "tech_camp" || eventType === "Tech Camp"
            ? "tech_camp"
            : "fdp";

        const res = await fetch(`${BASE_URL}/event/domainLists/${type}`, {
          headers,
        });
        if (res.ok) {
          const data = await res.json();
          const list = data?.domains || (Array.isArray(data) ? data : []);
          const match = list.find(
            (d) =>
              String(d.domain_name || d.name || "").trim().toLowerCase() ===
              String(domainName).trim().toLowerCase()
          );
          if (match && isMounted) {
            setResolvedDomainId(match.domain_id || match.id);
            return;
          }
        }
      } catch (err) {
        console.error("Error resolving domain id by name:", err);
      } finally {
        if (isMounted) setResolvingDomain(false);
      }
    };

    findDomainId();

    return () => {
      isMounted = false;
    };
  }, [open, booking, propDomainId, domainName, eventType]);

  const eventTypeLabel =
    eventType === "tech_camp" || eventType === "Tech Camp"
      ? "Tech Camp"
      : "FDP";

  const eligibility = checkPrerequisitesViewEligibility(booking);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "14px",
          overflow: "hidden",
          minHeight: "520px",
          maxHeight: "92vh",
          backgroundColor: "#f8fafc"
        }
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          py: 1.75,
          px: 3,
          borderBottom: "1px solid #e2e8f0",
          backgroundColor: "#ffffff"
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <DocIcon sx={{ color: "#1877f2", fontSize: 24 }} />
          <Typography sx={{ fontWeight: 700, fontSize: "1.05rem", color: "#0f172a" }}>
            {domainName} Prerequisites
          </Typography>
          <Chip
            label={eventTypeLabel}
            size="small"
            sx={{
              height: "20px",
              fontSize: "0.6875rem",
              fontWeight: 700,
              backgroundColor: eventTypeLabel === "Tech Camp" ? "#f0fdf4" : "#eff6ff",
              color: eventTypeLabel === "Tech Camp" ? "#16a34a" : "#1877f2",
              border: `1px solid ${
                eventTypeLabel === "Tech Camp" ? "#bbf7d0" : "#bfdbfe"
              }`,
              borderRadius: "4px"
            }}
          />
        </Box>
        <IconButton onClick={onClose} size="small" sx={{ color: "#94a3b8" }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent
        sx={{
          p: { xs: 1.5, sm: 2.5 },
          backgroundColor: "#f8fafc",
          minHeight: "auto",
          display: "flex",
          flexDirection: "column"
        }}
      >
        {!eligibility.canView ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              py: 8,
              px: 2,
              flex: 1
            }}
          >
            <Alert severity="warning" sx={{ maxWidth: 560, width: "100%", borderRadius: "10px" }}>
              {eligibility.message}
            </Alert>
          </Box>
        ) : resolvingDomain ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              py: 10,
              gap: 1.5,
              flex: 1
            }}
          >
            <CircularProgress size={32} sx={{ color: "#1877f2" }} />
            <Typography sx={{ fontSize: "0.85rem", color: "#64748b" }}>
              Loading prerequisites for {domainName}...
            </Typography>
          </Box>
        ) : (
          <TrainerDocumentEditor
            readOnly={true}
            role="spoc"
            domainId={resolvedDomainId}
            domainName={domainName}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};

export default DomainPrerequisitesViewModal;
